import { chunk } from 'lodash-es'
import { DisplayUeopt } from '../../../constants/enums'
import {
    isLevelBasic,
    isLevelGuest,
} from '../../../mfcLogic/userProperties/level'
import {
    openSignupDrawer,
    openTokensDrawer,
} from '../../../state/modals/state.drawer'
import {
    getGreetingBannerText,
    getPromotionalBannerText,
} from '../../../utilities/bannerText'
import { isTouchDevice } from '../../../utilities/browser'
import UserRow from '../Grid/UserRow'
import { getRowHeight, resolveDisplayContainer } from '../Grid/helpers'
import BannerRow from './BannerRow'

export const pickRandomUser = (list) =>
    list[Math.floor(Math.random() * list.length)]

type DisplayModels = {
    displayComponentProps?: any
    rowComponentProps?: any
    displayBanner?: boolean
}

export const PLACEHOLDER_KEY_PREFIX = 'placeholder-'

type FillViewportConfig = {
    modelsLoaded: boolean
    containerHeight: number
    columnCount: number
    rowHeight: number
}

/**
 * Pad the list out to the visible viewport with inert placeholder tiles
 * while the live list is still on its way. A seeded boot paints the
 * server's first page before the socket connects, but a large or dense
 * viewport mounts more tiles than were seeded, and the rows below them
 * would otherwise sit blank for the seconds until the first CAMS table
 * lands. Once `modelsLoaded` flips the padding is dropped and the live rows
 * take over. An empty list is never padded: that boot is still behind the
 * loading screen.
 */
export const fillViewportWithPlaceholders = (
    models: Array<any>,
    config: FillViewportConfig,
) => {
    const { modelsLoaded, containerHeight, columnCount, rowHeight } = config

    if (
        modelsLoaded ||
        !models.length ||
        !(containerHeight > 0) ||
        !(columnCount > 0) ||
        !(rowHeight > 0)
    ) {
        return models
    }

    const visibleRows = Math.ceil(containerHeight / rowHeight)
    const missing = visibleRows * columnCount - models.length

    if (missing <= 0) {
        return models
    }

    return [
        ...models,
        ...Array.from({ length: missing }, (_, index) => ({
            _key: `${PLACEHOLDER_KEY_PREFIX}${index}`,
            placeholder: true,
        })),
    ]
}

export const getRows = (props) => {
    const {
        forceGridRefresh,
        containerHeight,
        // containerWidth,
        displayHeight,
        gridPadding,
        gridDensity,
        columnWidth,
        columnCount,
        platform,
        getStyle,
        isMobile,
        display,
        // router,
        scale,
        level,
        username,
        data,
    } = props

    if (!data) {
        return []
    }

    const isGuest = isLevelGuest(level)
    const isBasic = isLevelBasic(level)

    // guests and basic members get the promotional banner; everyone else
    // gets a plain welcome
    const isPromotional = isGuest || isBasic

    const previewScrollEnabled = display === DisplayUeopt.default && isMobile

    const willShowBanner = isMobile

    const DisplayComponent = resolveDisplayContainer(display)

    const isAvatarOrTextView = display === 'default' || display === 'text'
    // modelsLoaded stays out of `other`: it drives the placeholder fill and
    // is not a tile prop. Callers that don't report it get no fill.
    const { models, modelsLoaded = true, ...other } = data

    const displayModels = (list, config: DisplayModels = {}) => {
        const { displayComponentProps, rowComponentProps, displayBanner } =
            config

        const rowHeight = getRowHeight({
            displayHeight,
            displayBanner,
            gridPadding,
            platform,
            display,
            scale,
        })

        const items = fillViewportWithPlaceholders(list, {
            modelsLoaded,
            containerHeight,
            columnCount,
            rowHeight,
        })

        return chunk(items, columnCount).map((data, index) => {
            const itemKey = `row-${index}`

            return {
                ...config,
                rowComponentProps: {
                    displayComponentProps: {
                        forceGridRefresh,
                        displayHeight,
                        gridPadding,
                        columnWidth,
                        columnCount,
                        ...displayComponentProps,
                    },
                    DisplayComponent,
                    displayHeight,
                    gridPadding,
                    columnWidth,
                    rowHeight,
                    getStyle,
                    data,
                    ...rowComponentProps,
                },
                RowComponent: UserRow,
                rowHeight,
                itemKey,
            }
        })
    }

    const displayBanner = (props = {}) => {
        const rowHeight = 60
        const bannerText = isPromotional
            ? getPromotionalBannerText(isGuest)
            : getGreetingBannerText(username)

        const onClick = !isPromotional
            ? undefined
            : isGuest
              ? () => openSignupDrawer()
              : () => openTokensDrawer()

        return {
            RowComponent: BannerRow,
            rowComponentProps: {
                text: bannerText,
                onClick,
                greeting: !isPromotional,
            },
            itemKey: 'banner',
            rowHeight,
            ...props,
        }
    }

    const rows = displayModels(models, {
        displayComponentProps: {
            ...other,
            previewScrollEnabled,
            enableLargePreview: !isMobile && !isTouchDevice,
            displayStatusBadge: isAvatarOrTextView,
            displayBookmarkHighlight: true,
            displayFriendHighlight: true,
            displayBookmarkBadge: true,
            displayFriendBadge: true,
            enablePin: true,
            gridDensity,
            getStyle,
        },
    })

    if (willShowBanner && previewScrollEnabled) {
        return [displayBanner(), ...rows]
    }

    return [...rows]
}
